import type { InvoicePayment, User, UserBalances } from 'payments-api/model';
import clsx from 'clsx';
import { InvoiceLineItemType } from 'payments-api/model/invoiceLineItemType';
import React, { useMemo } from 'react';

import type { CurrencyCode } from '@fragment/types/currencyCodes';
import { currencyPrecision as currencyPrecisionMap } from '@fragment/types/currencyCodes';

import type { ItemTableItem } from './ItemTable';
import {
  DirectionArrowCells,
  DirectionArrowHeaderCells,
} from './DirectionArrowCells';
import { FormatBillingAmount } from './formatBillingAmount';
import {
  invoiceTabGrowCell,
  kInvoiceTabDataRow,
  kInvoiceTabHeaderRow,
  kInvoiceTabHeaderText,
  UsersHeaderCell,
} from './invoiceTabLayout';
import { UserLabelCell } from './UserLabelCell';

export type SummaryCurrencyRow = {
  currency: CurrencyCode;
  direction: 'payin' | 'payout' | 'none';
  due: number;
  totalPaid: number;
  remaining: number;
};

export type SummaryUserGroup = {
  externalId: string;
  role: string | undefined;
  currencyRows: SummaryCurrencyRow[];
};

export const buildSummaryUserGroup = (
  userBalance: UserBalances,
  items: ItemTableItem[],
  payments: InvoicePayment[],
  users: User[]
): SummaryUserGroup => {
  const userExternalId = userBalance.external_id;
  const user = users.find((u) => u.external_id === userExternalId);

  const userItems = items.filter((it) => it.user_id === userExternalId);
  const userPayments = payments.filter(
    (p) => p.user.external_id === userExternalId
  );

  const payinByCurrency = userItems
    .filter((it) => it.type === InvoiceLineItemType.payin)
    .reduce((acc, it) => {
      const code = it.currencyCode;
      acc[code] = (acc[code] || 0) + Number(it.amount);
      return acc;
    }, {} as Record<CurrencyCode, number>);

  const payoutByCurrency = userItems
    .filter((it) => it.type === InvoiceLineItemType.payout)
    .reduce((acc, it) => {
      const code = it.currencyCode;
      acc[code] = (acc[code] || 0) + Number(it.amount);
      return acc;
    }, {} as Record<CurrencyCode, number>);

  const paidByCurrency = userPayments.reduce((acc, p) => {
    const code = p.currency;
    acc[code] = (acc[code] || 0) + Number(p.amount);
    return acc;
  }, {} as Record<CurrencyCode, number>);

  const currencies = new Set<CurrencyCode>([
    ...(Object.keys(payinByCurrency) as CurrencyCode[]),
    ...(Object.keys(payoutByCurrency) as CurrencyCode[]),
    ...(Object.keys(paidByCurrency) as CurrencyCode[]),
  ]);

  const currencyRows: SummaryCurrencyRow[] = Array.from(currencies)
    .sort((a, b) => a.localeCompare(b))
    .map((currency) => {
      const payinSum = payinByCurrency[currency] || 0;
      const payoutSum = payoutByCurrency[currency] || 0;

      let direction: SummaryCurrencyRow['direction'] = 'none';
      if (payinSum > payoutSum) direction = 'payin';
      else if (payoutSum > payinSum) direction = 'payout';

      const due = Math.abs(payinSum - payoutSum);
      const totalPaid = paidByCurrency[currency] || 0;
      const remaining = due - totalPaid;

      return {
        currency,
        direction,
        due,
        totalPaid,
        remaining,
      };
    });

  return {
    externalId: userExternalId,
    role: user?.role,
    currencyRows,
  };
};

const summarySortKey = (
  group: SummaryUserGroup
): { direction: SummaryCurrencyRow['direction']; due: number } => {
  if (group.currencyRows.length === 0) {
    return { direction: 'none', due: 0 };
  }
  const directionOrder = { payin: 0, payout: 1, none: 2 } as const;
  let best = group.currencyRows[0];
  group.currencyRows.forEach((row) => {
    if (row.due > best.due) {
      best = row;
    } else if (row.due === best.due) {
      if (directionOrder[row.direction] < directionOrder[best.direction]) {
        best = row;
      }
    }
  });
  return { direction: best.direction, due: best.due };
};

/** Payins first (descending representative due), then payouts, then none. */
export const sortSummaryUserGroups = (
  groups: SummaryUserGroup[]
): SummaryUserGroup[] => {
  const order = { payin: 0, payout: 1, none: 2 } as const;
  return [...groups].sort((a, b) => {
    const ka = summarySortKey(a);
    const kb = summarySortKey(b);
    if (order[ka.direction] !== order[kb.direction]) {
      return order[ka.direction] - order[kb.direction];
    }
    return kb.due - ka.due;
  });
};

const headerStyling = (flexClass?: string) =>
  clsx(invoiceTabGrowCell(flexClass), 'justify-end text-main-500');

const AmountCell = ({
  amount,
  currency,
  flexClass,
}: {
  amount: number;
  currency: CurrencyCode | undefined;
  flexClass?: string;
}) => (
  <div
    className={clsx(
      kInvoiceTabHeaderText,
      invoiceTabGrowCell(flexClass),
      'justify-end'
    )}
  >
    {currency ? (
      <FormatBillingAmount
        amount={amount}
        currencyCode={currency}
        currencyPrecision={currencyPrecisionMap[currency]}
      />
    ) : (
      <span className="text-main-500 text-xmd">—</span>
    )}
  </div>
);

const summaryCurrencyLineClasses =
  'flex flex-row w-full items-center border-b-[0.5px] border-main-200 last:border-b-0 h-f4';

const SummaryCurrencyLine = ({
  row,
}: {
  row: SummaryCurrencyRow;
}): JSX.Element => (
  <div className={summaryCurrencyLineClasses}>
    <DirectionArrowCells direction={row.direction} />
    <div className={invoiceTabGrowCell('flex-[3]')} />
    <AmountCell
      amount={row.due}
      currency={row.currency}
      flexClass="flex-[0.5]"
    />
    <AmountCell amount={row.totalPaid} currency={row.currency} />
    <AmountCell amount={row.remaining} currency={row.currency} />
  </div>
);

const EmptySummaryAmountLine = (): JSX.Element => (
  <div className={summaryCurrencyLineClasses}>
    <DirectionArrowCells direction="none" />
    <div className={invoiceTabGrowCell('flex-[3]')} />
    <AmountCell amount={0} currency={undefined} flexClass="flex-[0.5]" />
    <AmountCell amount={0} currency={undefined} />
    <AmountCell amount={0} currency={undefined} />
  </div>
);

export const InvoiceSummaryTab = ({
  users,
  userBalances,
  items,
  payments,
  onUserClick,
}: {
  users: User[];
  userBalances: UserBalances[];
  items: ItemTableItem[];
  payments: InvoicePayment[];
  onUserClick?: (externalId: string) => void;
}): JSX.Element => {
  const groups = useMemo(
    () =>
      sortSummaryUserGroups(
        userBalances.map((ub) =>
          buildSummaryUserGroup(ub, items, payments, users)
        )
      ),
    [userBalances, items, payments, users]
  );

  return (
    <div className="flex flex-col h-fit w-full">
      <div className={kInvoiceTabHeaderRow}>
        <UsersHeaderCell />
        <DirectionArrowHeaderCells />
        <div className={headerStyling('flex-[3]')} />
        <span className={headerStyling('flex-[0.5]')}>Due</span>
        <span className={headerStyling()}>Total Paid</span>
        <span className={headerStyling()}>Remaining</span>
      </div>
      {groups.map((group) => (
        <div key={group.externalId} className={kInvoiceTabDataRow}>
          <UserLabelCell
            externalId={group.externalId}
            role={group.role}
            onClick={onUserClick}
            align={group.currencyRows.length > 1 ? 'start' : 'center'}
          />
          <div className="flex flex-col flex-1 min-w-0">
            {group.currencyRows.length === 0 ? (
              <EmptySummaryAmountLine />
            ) : (
              group.currencyRows.map((row) => (
                <SummaryCurrencyLine key={row.currency} row={row} />
              ))
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
